import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Brain,
  Workflow,
  Database,
  Mail,
  FileText,
  MessageSquare,
  Calendar,
  ListChecks,
  Phone,
  Plus,
  Check,
  Sparkles,
  TrendingUp,
  Settings,
  Users,
  GraduationCap,
  UserCircle2,
  Zap,
  Quote,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  aiosHero,
  aiosGap,
  aiosInstalled,
  aiosPhases,
  aiosOperator,
  aiosCta,
  aiosTestimonials,
} from '../mock';

const SectionEyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[#C2603A]">
    <span className="w-5 h-px bg-[#C2603A]/60" /> {children} <span className="w-5 h-px bg-[#C2603A]/60" />
  </div>
);

const sourceIcons = {
  Gmail: Mail,
  Drive: FileText,
  Slack: MessageSquare,
  Notion: FileText,
  CRM: Database,
  Calendar: Calendar,
  SOPs: ListChecks,
  '+ more': Plus,
};

const operatorIcons = [Settings, Zap, GraduationCap, UserCircle2];

const AIOS = () => {
  return (
    <main className="bg-cream sans-body text-ink">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-5 pt-16 md:pt-24 pb-20 md:pb-28 text-center fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-ink-10 px-4 py-1.5 text-[12px] text-muted">
            <Sparkles className="w-3.5 h-3.5 text-[#C2603A]" /> {aiosHero.badge}
          </div>
          <div className="mt-6">
            <SectionEyebrow>{aiosHero.eyebrow}</SectionEyebrow>
          </div>
          <h1 className="serif-display text-[44px] sm:text-6xl md:text-7xl leading-[1.05] mt-4 text-ink">
            {aiosHero.title}{' '}
            <span className="italic text-[#C2603A]">{aiosHero.titleAccent}</span>
          </h1>
          <div className="mx-auto mt-8 h-[3px] w-16 rounded-full bg-[#C2603A]/80" />
          <p className="mx-auto mt-8 max-w-2xl text-[17px] md:text-[18px] leading-relaxed text-muted">
            {aiosHero.description}
          </p>
          <div className="mt-10">
            <Link
              to={aiosHero.primaryCta.href}
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              {aiosHero.primaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GAP SECTION */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>{aiosGap.eyebrow}</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">{aiosGap.title}</h2>
            <p className="mt-5 text-muted max-w-2xl mx-auto leading-relaxed">
              {aiosGap.description}
            </p>
          </div>

          {/* Source chips */}
          <div className="mt-12 flex flex-wrap justify-center gap-2.5">
            {aiosGap.sources.map((s) => {
              const Icon = sourceIcons[s] || Plus;
              return (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-ink-10 px-3.5 py-1.5 text-[13px] text-ink/80 soft-card"
                >
                  <Icon className="w-3.5 h-3.5 text-[#C2603A]" /> {s}
                </span>
              );
            })}
          </div>

          {/* Compounding chart */}
          <div className="mt-14 rounded-2xl bg-white border border-ink-10 p-6 md:p-8 soft-card">
            <div className="flex flex-col md:flex-row items-start justify-between gap-3 mb-6">
              <div>
                <div className="text-[12px] uppercase tracking-wider text-muted">Company intelligence accumulated</div>
                <div className="text-[12px] text-muted">Time since day 1</div>
              </div>
              <div className="flex items-center gap-4 text-[12px]">
                <span className="inline-flex items-center gap-1.5 text-muted">
                  <span className="w-3 h-0.5 bg-ink/30" /> Scattered Context
                </span>
                <span className="inline-flex items-center gap-1.5 text-ink">
                  <span className="w-3 h-0.5 bg-[#C2603A]" /> Second Brain
                </span>
              </div>
            </div>

            {/* SVG chart */}
            <div className="relative">
              <svg viewBox="0 0 800 240" className="w-full h-auto" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="brainLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#E08C5F" />
                    <stop offset="100%" stopColor="#C2603A" />
                  </linearGradient>
                  <linearGradient id="brainFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C2603A" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#C2603A" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* X axis */}
                <line x1="40" y1="200" x2="780" y2="200" stroke="rgba(0,0,0,0.08)" strokeWidth="1" />
                {/* Scattered flat line */}
                <path
                  d="M 40 175 L 220 172 L 400 170 L 580 168 L 780 165"
                  stroke="rgba(0,0,0,0.25)"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                  fill="none"
                />
                {/* Second Brain compounding curve - fill */}
                <path
                  d="M 40 185 Q 200 175 320 140 T 580 60 L 780 25 L 780 200 L 40 200 Z"
                  fill="url(#brainFill)"
                />
                {/* Second Brain compounding curve - line */}
                <path
                  d="M 40 185 Q 200 175 320 140 T 580 60 L 780 25"
                  stroke="url(#brainLine)"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
                {/* Day 14 marker */}
                <line x1="120" y1="40" x2="120" y2="200" stroke="rgba(194, 96, 58, 0.3)" strokeWidth="1" strokeDasharray="3 3" />
                <circle cx="120" cy="183" r="5" fill="#fff" stroke="#C2603A" strokeWidth="2" />
              </svg>
              {/* Labels */}
              <div className="grid grid-cols-5 mt-2 text-[10px] sm:text-[11px] text-muted">
                <div className="text-center">Day 14<br /><span className="text-[#C2603A]">handoff</span></div>
                <div className="text-center">Month 1</div>
                <div className="text-center">Month 3</div>
                <div className="text-center">Month 6</div>
                <div className="text-center">Year 1</div>
              </div>
            </div>

            {/* Timeline notes */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {aiosGap.timeline.map((t) => (
                <div key={t.label} className="rounded-xl bg-[#FBF5EC] border border-ink-10 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-[#C2603A] font-semibold">{t.label}</div>
                  <div className="mt-1.5 text-[12.5px] text-ink/85 leading-snug">{t.text}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-8 text-center text-muted max-w-2xl mx-auto text-[14.5px]">
            {aiosGap.footer}
          </p>
        </div>
      </section>

      {/* WHAT GETS INSTALLED */}
      <section className="relative py-20 md:py-28 bg-[#F6EFE4]">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>{aiosInstalled.eyebrow}</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">{aiosInstalled.title}</h2>
            <p className="mt-5 text-muted max-w-2xl mx-auto leading-relaxed">{aiosInstalled.description}</p>
          </div>

          {/* Architecture diagram */}
          <div className="mt-14 rounded-2xl bg-white border border-ink-10 p-6 md:p-8 soft-card">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
              {/* Inputs */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted mb-3">Seed imports</div>
                <div className="flex flex-wrap gap-2">
                  {['Past Contracts', 'Email Archives', 'Call Library', 'Slack / Teams', 'Spreadsheets', 'PDF Library'].map((s) => (
                    <span key={s} className="text-[12px] px-2.5 py-1 rounded-md bg-[#FBF5EC] border border-ink-10 text-ink/80">{s}</span>
                  ))}
                </div>
              </div>

              {/* Brain */}
              <div className="flex flex-col items-center">
                <div className="hidden md:block w-px h-6 bg-[#C2603A]/30 mb-2" />
                <div className="rounded-2xl bg-gradient-to-br from-[#FCE7D6] to-[#F8D2B0] border border-[#C2603A]/30 p-5 text-center min-w-[200px]">
                  <Brain className="w-7 h-7 text-[#C2603A] mx-auto" />
                  <div className="mt-2 serif-display text-xl">The Second Brain</div>
                  <div className="text-[11px] text-[#8a3a1c] uppercase tracking-wider mt-1">Live · Connected</div>
                </div>
                <div className="hidden md:block w-px h-6 bg-[#C2603A]/30 mt-2" />
              </div>

              {/* Outputs */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted mb-3 md:text-right">Outputs</div>
                <div className="flex flex-wrap gap-2 md:justify-end">
                  {['Your Team', 'Automations', 'CRM Updates', 'Reports'].map((s) => (
                    <span key={s} className="text-[12px] px-2.5 py-1 rounded-md bg-[#FBF5EC] border border-ink-10 text-ink/80">{s}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl bg-[#FFF5EC] border border-[#C2603A]/20 px-4 py-3 text-[13px] text-ink/85 text-center">
              <span className="font-medium text-[#C2603A]">Shared Context Layer:</span>{' '}
              customer history · products · SOPs · brand voice · templates · commercial rules
            </div>
          </div>

          {/* Deliverable cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            {aiosInstalled.deliverables.map((d, i) => (
              <article key={d.number} className="rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#C2603A] bg-[#FCE7D6]/60 border border-[#C2603A]/20 rounded-full px-3 py-1">
                    Deliverable <span className="font-semibold">{d.number}</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center">
                    {i === 0 ? <Brain className="w-5 h-5" /> : <Workflow className="w-5 h-5" />}
                  </div>
                </div>
                <div className="text-[11px] text-muted mt-4">{d.label}</div>
                <h3 className="serif-display text-2xl md:text-3xl mt-2">{d.title}</h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{d.description}</p>

                <div className="mt-5 pt-5 border-t border-ink-10 grid grid-cols-1 gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink/70 font-semibold mb-2">What your team gets</div>
                    <ul className="space-y-1.5">
                      {d.gets.map((g) => (
                        <li key={g} className="flex items-start gap-2 text-[13.5px] text-ink/85">
                          <Check className="w-3.5 h-3.5 mt-1 text-[#C2603A] shrink-0" /> {g}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink/70 font-semibold mb-2">At handoff</div>
                    <ul className="space-y-1.5">
                      {d.handoff.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-[13.5px] text-ink/85">
                          <Check className="w-3.5 h-3.5 mt-1 text-[#C2603A] shrink-0" /> {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* THE 14 DAYS */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>{aiosPhases.eyebrow}</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">{aiosPhases.title}</h2>
            <p className="mt-5 text-muted max-w-2xl mx-auto leading-relaxed">{aiosPhases.description}</p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            {aiosPhases.phases.map((p, i) => (
              <div key={p.number} className="relative">
                <article className="rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card border-l-4 border-l-[#C2603A] h-full">
                  <div className="flex items-center justify-between">
                    <div className="text-[#C2603A] font-semibold tracking-wide text-sm">Phase {p.number}</div>
                    <div className="text-[11px] uppercase tracking-wider text-[#C2603A] bg-[#FCE7D6]/60 border border-[#C2603A]/20 rounded-full px-3 py-1">
                      {p.days}
                    </div>
                  </div>
                  <h3 className="serif-display text-3xl mt-3">{p.title}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{p.description}</p>

                  <div className="mt-5 pt-4 border-t border-ink-10">
                    <div className="text-[11px] uppercase tracking-wider text-ink/70 font-semibold mb-2">Output</div>
                    <ul className="space-y-1.5">
                      {p.output.map((o) => (
                        <li key={o} className="flex items-start gap-2 text-[13.5px] text-ink/85">
                          <Check className="w-3.5 h-3.5 mt-1 text-[#C2603A] shrink-0" /> {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
                {i < aiosPhases.phases.length - 1 && (
                  <span className="hidden md:block absolute top-12 -right-2.5 w-5 h-5 rotate-45 border-t-2 border-r-2 border-[#C2603A]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATOR */}
      <section className="relative py-20 md:py-28 bg-[#F6EFE4]">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>{aiosOperator.eyebrow}</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">{aiosOperator.title}</h2>
            <p className="mt-5 text-muted max-w-2xl mx-auto leading-relaxed">{aiosOperator.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-14">
            {aiosOperator.roles.map((r, i) => {
              const Icon = operatorIcons[i % operatorIcons.length];
              return (
                <article key={r.number} className="rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FCE7D6] to-[#F8D2B0] text-[#C2603A] flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-[#C2603A] font-semibold tracking-wide text-sm">{r.number}</div>
                      <h3 className="serif-display text-2xl mt-1">{r.title}</h3>
                      <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{r.description}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-20 md:py-24">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-5">
          <div className="text-center">
            <SectionEyebrow>Early results</SectionEyebrow>
            <h2 className="serif-display text-3xl md:text-4xl mt-4">What teams say after the install.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            {aiosTestimonials.map((t, i) => (
              <article
                key={i}
                className="relative rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card"
              >
                <Quote className="absolute -top-3 left-6 w-8 h-8 text-[#C2603A]/30 bg-cream rounded-full p-1" />
                <p className="text-[15.5px] leading-relaxed text-ink/85 mt-2">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-5 pt-4 border-t border-ink-10 flex items-center justify-between">
                  <div>
                    <div className="text-[13.5px] font-semibold text-ink">{t.author}</div>
                    <div className="text-[12.5px] text-muted">{t.company}</div>
                  </div>
                  <div className="text-[11px] tracking-[0.12em] uppercase text-[#C2603A] bg-[#FCE7D6]/60 border border-[#C2603A]/20 rounded-full px-3 py-1">
                    Anonymized
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <SectionEyebrow>{aiosCta.eyebrow}</SectionEyebrow>
          <h2 className="serif-display text-4xl md:text-5xl mt-4">{aiosCta.title}</h2>
          <p className="mt-4 text-muted max-w-2xl mx-auto leading-relaxed">{aiosCta.description}</p>
          <div className="mt-8">
            <Link
              to={aiosCta.cta.href}
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              {aiosCta.cta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-4 text-[13px] text-muted">{aiosCta.subline}</div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default AIOS;
